import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "wouter";

/* ─── Copy Banks ────────────────────────────────────────────────────────── */

const PT_BR = {
  pageTitle: "Programa de Acesso Antecipado — Lexiun",
  back: "← Voltar para Lexiun",
  heading: "Programa de Acesso Antecipado",
  description:
    "Um grupo seleto de escritórios e organizações já está trabalhando com o Lexiun. Vagas de Acesso Antecipado permanecem abertas — por enquanto.",
  benefitsLabel: "Como parceiro de Acesso Antecipado",
  benefits: [
    "Você acessa o Lexiun antes de todos",
    "A equipe fundadora faz seu onboarding pessoalmente",
    "Seu feedback molda diretamente o produto",
    "Seu preço fica travado nas condições de fundação",
  ],
  closingNote:
    "Temos interesse especial em equipes que lidam com casos complexos, de alto volume ou alto risco — onde nenhum detalhe pode ser perdido.",
  formHeading: "Solicitar Acesso Antecipado",
  nameLabel: "Nome",
  namePlaceholder: "Seu nome completo",
  nameRequired: "Nome é obrigatório",
  emailLabel: "E-mail corporativo",
  emailPlaceholder: "voce@seuescritorio.com.br",
  emailInvalid: "Informe um e-mail corporativo válido",
  orgLabel: "Organização",
  orgPlaceholder: "Nome do escritório ou empresa",
  orgRequired: "Organização é obrigatória",
  roleLabel: "Eu sou…",
  rolePlaceholder: "Selecione sua função",
  roleRequired: "Por favor, selecione sua função",
  roles: [
    "Sócio Administrador",
    "Sócio",
    "Head de Inovação & Tecnologia",
    "Diretor Jurídico",
    "Head do Departamento Jurídico",
    "Diretor de Operações Jurídicas",
    "Associado",
    "Outro",
  ],
  interestLabel: "O que despertou seu interesse no Lexiun?",
  interestHelper: "Uma indicação, uma necessidade específica, curiosidade — vale tudo.",
  legalAiLabel: "Sua organização usa alguma solução de IA jurídica atualmente?",
  legalAiHelper: "Ex.: Harvey, Enter, CoCounsel, ChatGPT ou ferramentas internas.",
  challengeLabel:
    "Conte-nos sobre o maior desafio da sua organização onde a IA pode ajudar.",
  challengeHelper:
    "Volume de processos, retenção de conhecimento, prazos perdidos, sobrecarga de documentos — o que tira seu sono.",
  whatsappLabel:
    "Aceito ser contatado via WhatsApp para uma introdução mais rápida",
  whatsappPhonePlaceholder: "+55 11 99999-9999",
  whatsappPhoneRequired: "Número do WhatsApp é obrigatório quando o opt-in está marcado",
  submitting: "Enviando…",
  submit: "Enviar Solicitação",
  submitNote: "Nós revisamos pessoalmente cada solicitação. Você terá nosso retorno.",
  networkError:
    "Erro de rede. Tente novamente ou envie e-mail para contact@lexiun.ai",
  successLabel: "Solicitação Recebida",
  successHeading: "Obrigado, {name}.",
  successBody1:
    "Sua solicitação para o Programa de Acesso Antecipado do Lexiun foi recebida e está em análise.",
  successBody2:
    "Devido ao alto interesse, nosso processo de revisão pode levar alguns dias — mas revisamos e respondemos pessoalmente cada solicitação. Você terá nosso retorno.",
  successBody3:
    "Se seu perfil for uma boa combinação para nossa turma atual, um membro da equipe fundadora entrará em contato para agendar uma introdução pessoal ao Lexiun.",
  successContact: "Enquanto isso, entre em contato conosco:",
  returnLink: "← Voltar para Lexiun",
  copyright: "© 2026 Lexiun AI · São Paulo, Brasil",
};

const EN = {
  pageTitle: "Early Access Program — Lexiun",
  back: "← Back to Lexiun",
  heading: "Early Access Program",
  description:
    "A select group of law firms and organizations is already working with Lexiun. Early Access spots remain open — for now.",
  benefitsLabel: "As an Early Access partner",
  benefits: [
    "You get Lexiun before anyone else",
    "The founding team onboards you personally",
    "Your feedback directly shapes the product",
    "Your pricing is locked at founding rates",
  ],
  closingNote:
    "We're especially interested in teams that deal with complex, high-volume, or high-stakes cases — the kind where no detail can afford to be missed.",
  formHeading: "Apply for Early Access",
  nameLabel: "Name",
  namePlaceholder: "Your full name",
  nameRequired: "Name is required",
  emailLabel: "Work email",
  emailPlaceholder: "you@yourfirm.com",
  emailInvalid: "Enter a valid work email",
  orgLabel: "Organization",
  orgPlaceholder: "Firm or company name",
  orgRequired: "Organization is required",
  roleLabel: "I am a…",
  rolePlaceholder: "Select your role",
  roleRequired: "Please select your role",
  roles: [
    "Managing Partner",
    "Partner",
    "Head of Innovation & Technology",
    "General Counsel",
    "Head of Legal Department",
    "Legal Operations Director",
    "Associate",
    "Other",
  ],
  interestLabel: "What prompted your interest in Lexiun?",
  interestHelper: "A referral, a specific need, general curiosity — anything goes.",
  legalAiLabel: "Is your organization currently using any legal AI solutions?",
  legalAiHelper: "E.g. Harvey, Enter, CoCounsel, ChatGPT, or any internal tools.",
  challengeLabel:
    "Tell us about the biggest challenge in your organization where you think AI can help.",
  challengeHelper:
    "Case volume, knowledge retention, missed deadlines, document overload — tell us what keeps you up at night.",
  whatsappLabel:
    "I'm open to being contacted via WhatsApp for a faster introduction",
  whatsappPhonePlaceholder: "+55 11 99999-9999",
  whatsappPhoneRequired: "WhatsApp number is required when opt-in is checked",
  submitting: "Sending…",
  submit: "Submit Application",
  submitNote: "We personally review every application. You will hear from us.",
  networkError:
    "Network error. Please try again or email us directly at contact@lexiun.ai",
  successLabel: "Application Received",
  successHeading: "Thank you, {name}.",
  successBody1:
    "Your application for Lexiun's Early Access Program has been received and is now under review.",
  successBody2:
    "Due to exceptionally high interest, our review process may take a few days — but we personally review and respond to every single application. You will hear from us.",
  successBody3:
    "If your profile is a strong fit for our current cohort, a member of our founding team will reach out to schedule a personal introduction to Lexiun.",
  successContact: "In the meantime, feel free to reach us directly:",
  returnLink: "← Return to Lexiun",
  copyright: "© 2026 Lexiun AI · São Paulo, Brazil",
};

/* ─── Schema (dynamic messages) ─────────────────────────────────────────── */

function makeSchema(t: typeof EN) {
  return z
    .object({
      name: z.string().min(1, t.nameRequired),
      email: z.string().email(t.emailInvalid),
      organization: z.string().min(1, t.orgRequired),
      role: z.string().min(1, t.roleRequired),
      interest: z.string().optional(),
      legalAi: z.string().optional(),
      challenge: z.string().optional(),
      whatsapp: z.boolean().optional(),
      phone: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.whatsapp && !data.phone?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t.whatsappPhoneRequired,
          path: ["phone"],
        });
      }
    });
}

type FormData = z.infer<ReturnType<typeof makeSchema>>;

/* ─── Page ───────────────────────────────────────────────────────────────── */

export default function EarlyAccessPage({ lang, setLang: _setLang }: { lang: "en" | "pt"; setLang: (l: "en" | "pt") => void }) {
  const t = lang === "pt" ? PT_BR : EN;
  const schema = makeSchema(t);

  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successName, setSuccessName] = useState("");
  const firstInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- zod v3/v4 type mismatch in workspace
    resolver: zodResolver(schema as any),
    defaultValues: { whatsapp: false },
  });

  const whatsappChecked = watch("whatsapp");

  useEffect(() => {
    document.title = t.pageTitle;
    setTimeout(() => firstInputRef.current?.focus(), 120);
    return () => {
      document.title = "Lexiun";
    };
  }, [t.pageTitle]);

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch("/api/early-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) {
        setServerError(json.error ?? "Something went wrong. Please try again.");
        return;
      }
      setSuccessName(data.name.split(" ")[0]);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setServerError(t.networkError);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 3,
    color: "#F2EEE9",
    fontSize: "0.9375rem",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    padding: "11px 14px",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.75rem",
    color: "rgba(242,238,233,0.45)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    marginBottom: 6,
  };

  const helperStyle: React.CSSProperties = {
    fontSize: "0.78rem",
    color: "rgba(242,238,233,0.25)",
    lineHeight: 1.5,
    marginBottom: 7,
    fontFamily: "'DM Sans', system-ui, sans-serif",
  };

  const errorStyle: React.CSSProperties = {
    fontSize: "0.75rem",
    color: "#E07B5A",
    marginTop: 5,
    fontFamily: "'DM Sans', system-ui, sans-serif",
  };

  const fieldGap: React.CSSProperties = { marginBottom: 22 };

  const { ref: nameRef, ...nameRest } = register("name");

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0B0D11",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: "#F2EEE9",
      }}
    >
      <style>{`
        .eap-input:focus { outline: none; border-color: rgba(212,160,82,0.5) !important; box-shadow: 0 0 0 3px rgba(212,160,82,0.08); }
        .eap-input::placeholder { color: rgba(120,115,110,0.5); }
        .eap-select option { background: #1A1D24; color: #F2EEE9; }
        @media (max-width: 820px) {
          .eap-layout { flex-direction: column !important; }
          .eap-left { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.06) !important; padding-bottom: 40px !important; }
        }
      `}</style>

      {/* Nav */}
      <nav
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 clamp(20px, 5vw, 60px)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Link href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
          <img
            src="/lexiun-wordmark.svg"
            alt="Lexiun"
            width={120}
            height={32}
          />
        </Link>
        <Link
          href="/"
          style={{
            fontSize: "0.875rem",
            color: "rgba(242,238,233,0.65)",
            textDecoration: "none",
            letterSpacing: "0.01em",
            transition: "color 0.2s",
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 0",
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = "#F2EEE9")}
          onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.65)")}
        >
          {t.back}
        </Link>
      </nav>

      {/* Main layout */}
      <div
        className="eap-layout"
        style={{
          display: "flex",
          maxWidth: 1100,
          margin: "0 auto",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        {/* ── Left: programme description ── */}
        <div
          className="eap-left"
          style={{
            width: 380,
            flexShrink: 0,
            padding: "clamp(48px, 7vw, 80px) clamp(28px, 4vw, 56px)",
            borderRight: "1px solid rgba(255,255,255,0.05)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ width: 32, height: 2, backgroundColor: "#D4A052", marginBottom: 28 }} />

          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(1.8rem, 2.8vw, 2.4rem)",
              fontWeight: 400,
              color: "#F2EEE9",
              lineHeight: 1.18,
              letterSpacing: "-0.01em",
              marginBottom: 20,
            }}
          >
            {t.heading}
          </h1>

          <p
            style={{
              fontSize: "0.9375rem",
              color: "rgba(242,238,233,0.55)",
              lineHeight: 1.8,
              marginBottom: 36,
            }}
          >
            {t.description}
          </p>

          <p
            style={{
              fontSize: "0.75rem",
              color: "#D4A052",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            {t.benefitsLabel}
          </p>

          <div style={{ marginBottom: 40 }}>
            {t.benefits.map((benefit) => (
              <div key={benefit} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
                <span style={{ color: "#D4A052", fontSize: "0.7rem", flexShrink: 0, marginTop: 4 }}>✦</span>
                <span style={{ fontSize: "0.9rem", color: "rgba(242,238,233,0.7)", lineHeight: 1.6 }}>{benefit}</span>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 28 }}>
            <p style={{ fontSize: "0.85rem", color: "rgba(242,238,233,0.3)", lineHeight: 1.75, fontStyle: "italic" }}>
              {t.closingNote}
            </p>
          </div>
        </div>

        {/* ── Right: form or success ── */}
        <div style={{ flex: 1, padding: "clamp(48px, 7vw, 80px) clamp(28px, 5vw, 64px)" }}>
          {success ? (
            <SuccessState name={successName} t={t} />
          ) : (
            <>
              <h2
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)",
                  fontWeight: 400,
                  color: "#F2EEE9",
                  letterSpacing: "-0.01em",
                  marginBottom: 32,
                }}
              >
                {t.formHeading}
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Name */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.nameLabel}</label>
                  <input
                    {...nameRest}
                    ref={(el) => {
                      nameRef(el);
                      (firstInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }}
                    type="text"
                    autoComplete="name"
                    placeholder={t.namePlaceholder}
                    className="eap-input"
                    style={{ ...inputStyle, borderColor: errors.name ? "#E07B5A" : "rgba(255,255,255,0.1)" }}
                  />
                  {errors.name && <p style={errorStyle}>{errors.name.message}</p>}
                </div>

                {/* Work email */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.emailLabel}</label>
                  <input
                    {...register("email")}
                    type="email"
                    autoComplete="email"
                    placeholder={t.emailPlaceholder}
                    className="eap-input"
                    style={{ ...inputStyle, borderColor: errors.email ? "#E07B5A" : "rgba(255,255,255,0.1)" }}
                  />
                  {errors.email && <p style={errorStyle}>{errors.email.message}</p>}
                </div>

                {/* Organization */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.orgLabel}</label>
                  <input
                    {...register("organization")}
                    type="text"
                    autoComplete="organization"
                    placeholder={t.orgPlaceholder}
                    className="eap-input"
                    style={{ ...inputStyle, borderColor: errors.organization ? "#E07B5A" : "rgba(255,255,255,0.1)" }}
                  />
                  {errors.organization && <p style={errorStyle}>{errors.organization.message}</p>}
                </div>

                {/* Role */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.roleLabel}</label>
                  <select
                    {...register("role")}
                    className="eap-input eap-select"
                    style={{
                      ...inputStyle,
                      appearance: "none",
                      WebkitAppearance: "none",
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237A7570' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 14px center",
                      paddingRight: 38,
                      borderColor: errors.role ? "#E07B5A" : "rgba(255,255,255,0.1)",
                      cursor: "pointer",
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>{t.rolePlaceholder}</option>
                    {t.roles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {errors.role && <p style={errorStyle}>{errors.role.message}</p>}
                </div>

                {/* Interest */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.interestLabel}</label>
                  <p style={helperStyle}>{t.interestHelper}</p>
                  <textarea
                    {...register("interest")}
                    rows={2}
                    className="eap-input"
                    style={{ ...inputStyle, resize: "vertical", minHeight: 72 }}
                  />
                </div>

                {/* Legal AI */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.legalAiLabel}</label>
                  <p style={helperStyle}>{t.legalAiHelper}</p>
                  <textarea
                    {...register("legalAi")}
                    rows={2}
                    className="eap-input"
                    style={{ ...inputStyle, resize: "vertical", minHeight: 72 }}
                  />
                </div>

                {/* Challenge */}
                <div style={fieldGap}>
                  <label style={labelStyle}>{t.challengeLabel}</label>
                  <p style={helperStyle}>{t.challengeHelper}</p>
                  <textarea
                    {...register("challenge")}
                    rows={4}
                    className="eap-input"
                    style={{ ...inputStyle, resize: "vertical", minHeight: 112 }}
                  />
                </div>

                {/* WhatsApp opt-in */}
                <div style={{ ...fieldGap, marginBottom: 32 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input {...register("whatsapp")} type="checkbox" style={{ display: "none" }} />
                    <span
                      style={{
                        width: 16, height: 16, flexShrink: 0, marginTop: 2,
                        border: `1px solid ${whatsappChecked ? "#D4A052" : "rgba(255,255,255,0.2)"}`,
                        borderRadius: 3,
                        background: whatsappChecked ? "#D4A052" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "background 0.15s, border-color 0.15s",
                      }}
                    >
                      {whatsappChecked && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <path d="M1 3.5L3.5 6L8 1" stroke="#0B0D11" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span style={{ fontSize: "0.875rem", color: "rgba(242,238,233,0.6)", lineHeight: 1.55 }}>
                      {t.whatsappLabel}
                    </span>
                  </label>

                  {whatsappChecked && (
                    <div style={{ marginTop: 12, paddingLeft: 26 }}>
                      <input
                        {...register("phone")}
                        type="tel"
                        autoComplete="tel"
                        placeholder={t.whatsappPhonePlaceholder}
                        className="eap-input"
                        style={{ ...inputStyle, borderColor: errors.phone ? "#E07B5A" : "rgba(255,255,255,0.1)" }}
                      />
                      {errors.phone && <p style={errorStyle}>{errors.phone.message}</p>}
                    </div>
                  )}
                </div>

                {/* Server error */}
                {serverError && (
                  <div
                    style={{
                      background: "rgba(224,123,90,0.08)",
                      border: "1px solid rgba(224,123,90,0.25)",
                      borderRadius: 3,
                      padding: "12px 16px",
                      marginBottom: 20,
                      fontSize: "0.875rem",
                      color: "#E09580",
                      lineHeight: 1.55,
                    }}
                  >
                    {serverError}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: "14px 40px",
                    background: submitting ? "rgba(212,160,82,0.5)" : "#D4A052",
                    color: "#0B0D11",
                    border: "none",
                    borderRadius: 3,
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    cursor: submitting ? "default" : "pointer",
                    transition: "background 0.2s, transform 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      (e.currentTarget as HTMLButtonElement).style.background = "#E0B468";
                      (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      (e.currentTarget as HTMLButtonElement).style.background = "#D4A052";
                      (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                    }
                  }}
                >
                  {submitting ? t.submitting : t.submit}
                </button>

                <p style={{ marginTop: 14, fontSize: "0.75rem", color: "rgba(242,238,233,0.2)", lineHeight: 1.5 }}>
                  {t.submitNote}
                </p>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.04)",
          padding: "20px clamp(20px, 5vw, 60px)",
          textAlign: "center",
        }}
      >
        <p style={{ fontSize: "0.75rem", color: "#2A2622", letterSpacing: "0.04em" }}>
          {t.copyright}
        </p>
      </div>
    </div>
  );
}

/* ─── Success State ──────────────────────────────────────────────────────── */

function SuccessState({ name, t }: { name: string; t: typeof EN }) {
  return (
    <div style={{ maxWidth: 520 }}>
      <div
        style={{
          width: 52, height: 52, borderRadius: "50%",
          border: "1px solid rgba(212,160,82,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 32,
        }}
      >
        <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
          <path d="M1.5 8.5L8 15L20.5 1" stroke="#D4A052" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <p style={{ fontSize: "0.72rem", color: "#D4A052", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 16 }}>
        {t.successLabel}
      </p>

      <h2
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
          fontWeight: 400, color: "#F2EEE9",
          lineHeight: 1.18, letterSpacing: "-0.01em",
          marginBottom: 28,
        }}
      >
        {t.successHeading.replace("{name}", name)}
      </h2>

      <div style={{ fontSize: "0.9375rem", color: "rgba(242,238,233,0.55)", lineHeight: 1.85 }}>
        <p style={{ marginBottom: 16 }}>{t.successBody1}</p>
        <p style={{ marginBottom: 16 }}>{t.successBody2}</p>
        <p style={{ marginBottom: 28 }}>{t.successBody3}</p>
        <p style={{ fontSize: "0.875rem" }}>
          {t.successContact}{" "}
          <a href="mailto:contact@lexiun.ai" style={{ color: "#D4A052", textDecoration: "none" }}>
            contact@lexiun.ai
          </a>
        </p>
      </div>

      <Link
        href="/"
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          marginTop: 40, padding: "12px 28px",
          background: "transparent", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 3, color: "rgba(242,238,233,0.5)",
          fontSize: "0.875rem", textDecoration: "none",
          transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.22)";
          (e.currentTarget as HTMLAnchorElement).style.color = "#F2EEE9";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.1)";
          (e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.5)";
        }}
      >
        {t.returnLink}
      </Link>
    </div>
  );
}
